/**
 * Disruption Report — who blocked whom, how often, and for how long.
 *
 * Two honesty notes are rendered rather than hidden:
 *
 *  - ATTEMPTS are ledger-backed, so a game played before migration 048 shows an
 *    explicit notice in that column instead of a zero.
 *  - Some LANDED rows carry a corrupt clock. `disrupted_at` used to be written
 *    from the victim's combat log, so a client with a wrong log date stamped a
 *    disruption days from reality. Those rows still count as disruptions — they
 *    really happened — but are excluded from the durations, and the exclusion is
 *    stated so a partial average is never mistaken for a complete one.
 */

import type { TrlDisruptionStats } from '@swtorbit/shared-socket-events';
import { StatCard } from './StatCard';
import { formatMs } from './statsFormat';

const REASON_LABELS: Record<string, string> = {
  'already-blocked': 'already blocked',
  'already-disrupted': 'already used on them',
  'not-casting': 'not casting',
};

export function TrlDisruptionCard({ disruption }: { disruption: TrlDisruptionStats }) {
  const attemptsRecorded = disruption.attemptAvailability === 'recorded';

  return (
    <StatCard
      title="Disruption Report"
      icon="✨"
      summary={
        attemptsRecorded
          ? `${disruption.totalLanded} landed · ${disruption.totalAttempts} missed`
          : `${disruption.totalLanded} landed`
      }
      isEmpty={disruption.byDisruptor.length === 0}
      emptyMessage="Nobody disrupted anybody in this game."
    >
      <div className="d-flex flex-column gap-xs">
        <div className="d-flex gap-sm flex-wrap fs-sm">
          <span className="text-cyan">
            Average block: <span className="text-white">{formatMs(disruption.averageBlockMs)}</span>
          </span>
          <span className="text-cyan">
            Longest: <span className="text-white">{formatMs(disruption.longestBlockMs)}</span>
          </span>
        </div>

        {disruption.outOfWindowCount > 0 && (
          <span className="fs-xs text-amber">
            ⚠️ {disruption.outOfWindowCount} of {disruption.totalLanded} landed disruptions carry a
            timestamp outside the game window — a known client-clock fault. They are counted below
            but excluded from the durations above and from the timeline.
          </span>
        )}

        <div className="d-flex flex-column gap-2xs">
          <div className="d-flex gap-xs fs-xs text-slate px-xs">
            <span className="flex-1">Disruptor</span>
            <span style={{ minWidth: '9rem' }}>Team</span>
            <span style={{ minWidth: '4rem' }}>Landed</span>
            <span style={{ minWidth: '12rem' }}>Missed</span>
          </div>

          {disruption.byDisruptor.map((d) => {
            const reasons = Object.entries(d.attemptsByReason);
            return (
              <div
                key={d.playerId}
                className="d-flex gap-xs align-items-center px-xs py-2xs rounded-2xs flex-wrap"
                style={{ background: 'color-mix(in srgb, var(--color-white) 4%, transparent)' }}
              >
                <span className="text-white flex-1">{d.characterName}</span>
                <span className="text-cyan fs-sm" style={{ minWidth: '9rem' }}>{d.teamName}</span>
                <span className="text-white fs-sm" style={{ minWidth: '4rem' }}>{d.landed}</span>
                <span className="fs-xs text-slate" style={{ minWidth: '12rem' }}>
                  {!attemptsRecorded
                    ? 'not recorded'
                    : reasons.length === 0
                      ? '—'
                      : reasons
                          .map(([reason, count]) => `${count} ${REASON_LABELS[reason] ?? reason}`)
                          .join(' · ')}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </StatCard>
  );
}
